import { useQuery } from '@tanstack/react-query';
import DOMPurify from 'dompurify';
import { useEffect, useMemo } from 'react';
import { useAppContext } from 'src/contexts/AppContext';
import { useClientLogContext } from 'src/contexts/ClientLogContext';
import { PosSpinner } from 'src/core/POS/PosSpinner';
import { vars } from 'src/core/themes';
import { useUserHasFeature } from 'src/hooks/useUserHasFeature';
import { AddFileIcon } from 'src/svgs/AddFileIcon';
import { tryInvokeApi } from 'src/utils/tryExecuteUtils';
import {
  Feature,
  ReachProSupportCaseAttachmentResponse,
  ReachProSupportCaseClient,
} from 'src/WebApiController';

import * as styles from './ReachProSupportCaseMessage.css';

export interface CaseMessage {
  id: string;
  content: string;
  senderName: string;
  timestamp: Date;
  isSent: boolean;
}

interface ReachProSupportCaseMessageProps {
  message: CaseMessage;
  workItemId?: number;
  attachments?: ReachProSupportCaseAttachmentResponse[];
}

interface AttachmentItemProps {
  workItemId: number;
  attachment: ReachProSupportCaseAttachmentResponse;
}

function AttachmentItem({ workItemId, attachment }: AttachmentItemProps) {
  const { activeAccountWebClientConfig } = useAppContext();
  const { trackError } = useClientLogContext();
  const hasReachProSupportFeature = useUserHasFeature(Feature.ReachProSupport);

  const { data: objectUrl, isLoading } = useQuery({
    queryKey: [
      'reachpro-support-case-attachment',
      workItemId,
      attachment.documentId,
    ],
    queryFn: async () => {
      if (!hasReachProSupportFeature) {
        return null;
      }
      const response = await tryInvokeApi(
        async () => {
          return await new ReachProSupportCaseClient(
            activeAccountWebClientConfig
          ).getSupportCaseAttachment(workItemId, attachment.documentId);
        },
        (error) => {
          trackError(
            'ReachProSupportCaseClient.getSupportCaseAttachment',
            error
          );
        }
      );
      if (!response?.data) {
        return null;
      }
      return URL.createObjectURL(response.data);
    },
    enabled: hasReachProSupportFeature,
    // Object URLs should not be stale-reused across mounts; re-fetch each time
    staleTime: 0,
    gcTime: 0,
  });

  // Revoke the object URL when the component unmounts or when the URL changes
  useEffect(() => {
    return () => {
      if (objectUrl) {
        URL.revokeObjectURL(objectUrl);
      }
    };
  }, [objectUrl]);

  if (isLoading) {
    return (
      <div className={styles.attachmentPlaceholder}>
        <PosSpinner size={vars.iconSize.m} />
      </div>
    );
  }

  if (!objectUrl) {
    return null;
  }

  if (attachment.isImage) {
    return (
      <a
        href={objectUrl}
        download={attachment.fileName}
        className={styles.thumbnailAnchor}
        title={attachment.fileName}
      >
        <img
          src={objectUrl}
          alt={attachment.fileName}
          className={styles.thumbnailImage}
        />
      </a>
    );
  }

  return (
    <a
      href={objectUrl}
      download={attachment.fileName}
      className={styles.fileChip}
      title={attachment.fileName}
    >
      <AddFileIcon size={vars.iconSize.m} />
      <span className={styles.fileChipName}>{attachment.fileName}</span>
    </a>
  );
}

export function ReachProSupportCaseMessage({
  message,
  workItemId,
  attachments,
}: ReachProSupportCaseMessageProps) {
  const sanitizedContent = useMemo(() => {
    return DOMPurify.sanitize(message.content, {
      ALLOWED_TAGS: [
        'br',
        'div',
        'p',
        'span',
        'b',
        'i',
        'strong',
        'em',
        'ul',
        'ol',
        'li',
        'a',
      ],
      ALLOWED_ATTR: ['href', 'target', 'rel'],
    });
  }, [message.content]);

  const hasAttachments =
    workItemId != null && attachments != null && attachments.length > 0;

  return (
    <div
      className={`${styles.messageBubble} ${
        message.isSent ? styles.messageBubbleSent : styles.messageBubbleReceived
      }`}
    >
      <div className={styles.messageHeader}>
        {message.senderName}
        <span className={styles.messageTimestamp}>
          {' • '}
          {message.timestamp.toLocaleString([], {
            month: '2-digit',
            day: '2-digit',
            year: 'numeric',
            hour: 'numeric',
            minute: '2-digit',
          })}
        </span>
      </div>
      <div
        className={styles.messageContent}
        dangerouslySetInnerHTML={{ __html: sanitizedContent }}
      />
      {hasAttachments && (
        <div className={styles.attachmentsRow}>
          {attachments!.map((attachment) => (
            <AttachmentItem
              key={attachment.documentId}
              workItemId={workItemId!}
              attachment={attachment}
            />
          ))}
        </div>
      )}
    </div>
  );
}
